// Quick Sizer models. Wizard and NGXS shapes, plus the nested API request/response.

/** Quick Sizer is vSAN ESA only. ReadyNode search, the engine and dataprovider all use `ESA`. */
export const QS_VSAN_ESA = 'ESA';
/** The dataprovider components call takes the product architecture. */
export const QS_DATAPROVIDER_COMPONENTS_ARCHITECTURE = 'STANDARD';
export const QS_PRODUCT_NAME = 'VCF';
export const QS_PRODUCT_VERSION = '9.1.0';

/** Dataprovider `/configurations` query params. Must match the forward sizer's defaults. */
export const QS_DATAPROVIDER_STORAGE_TYPE = 'VSAN';
export const QS_DATAPROVIDER_WORKLOAD_OPTION = 'GPW';
export const QS_DATAPROVIDER_DEPLOYMENT_CODE = 'GVM';
export const QS_DATAPROVIDER_PROFILE_MODE_CODE = 'AF';

// ── VCF component overheads ──

/** One backend component inside a VCF components card. */
export interface DomainComponentPart {
  productName: string;
  label: string;
  brickSize: string;
  /** Catalog default size, set only when the engine charged a bigger one. */
  defaultBrickSize?: string;
  vms: number;
  /** Sum of vCPU over the part's VMs. Not physical cores. */
  vcpu: number;
  memoryGB: number;
  storageGB: number;
  /** No catalog row matched, so the part is shown as unavailable and left out of the request. */
  missingSizing: boolean;
}

/** One VCF components card, built from a forward-sizer `VcfDomain.SubGroup`. Resource fields roll up `parts`. */
export interface DomainComponent {
  /** `subGroupId`, unique per domain. */
  cardId: string;
  title: string;
  isRequired: boolean;
  enabledByDefault: boolean;
  parts: DomainComponentPart[];
  brickSize: string;
  vms: number;
  /** Sum of vCPU over all parts. Not physical cores. */
  vcpu: number;
  memoryGB: number;
  storageGB: number;
}

// ── Storage configuration ──

export interface FttRaidOption {
  label: string;
  ftt: number;
  raid: string;
  hostRebuildReserveEnabled: boolean;
  overheadRatio: number;
  minClusterSize: number;
}

/** `fttRaid` value for Auto: the engine picks FTT/RAID from the host count. */
export const QS_FTT_RAID_AUTO = 'AUTO';

export function fttRaidKey(option: Pick<FttRaidOption, 'ftt' | 'raid'>): string {
  return `${option.ftt}-${option.raid}`;
}

export function parseFttRaidKey(key: string): { ftt: number; raid: string } | null {
  const dash = key.indexOf('-');
  if (dash <= 0) {
    return null;
  }
  const ftt = Number(key.slice(0, dash));
  const raid = key.slice(dash + 1);
  if (!Number.isFinite(ftt) || !raid) {
    return null;
  }
  return { ftt, raid };
}

/** No `NONE`: the forward sizer does not offer it for vSAN ESA. */
export type StorageEfficiency = 'COMPRESSION_ONLY' | 'DEDUP_AND_COMPRESSION';

/** The forward sizer's ESA default. */
export const DEFAULT_STORAGE_EFFICIENCY: StorageEfficiency = 'COMPRESSION_ONLY';

/** Same wording as the forward sizer. */
export function storageEfficiencyLabel(option: StorageEfficiency): string {
  switch (option) {
    case 'COMPRESSION_ONLY':
      return 'Compression Only';
    case 'DEDUP_AND_COMPRESSION':
      return 'Dedup and Compression';
  }
}

/** Data-reduction ratio range per mode. Dedup + Compression is the combined saving, so its floor is 2. */
export const DATA_REDUCTION_RATIO_BOUNDS: Record<
  StorageEfficiency,
  { default: number; min: number; max: number }
> = {
  COMPRESSION_ONLY: { default: 1.3, min: 1, max: 8 },
  DEDUP_AND_COMPRESSION: { default: 2.9, min: 2, max: 6 },
};

export function dataReductionRatioBounds(
  efficiency: StorageEfficiency,
): { default: number; min: number; max: number } {
  return DATA_REDUCTION_RATIO_BOUNDS[efficiency];
}

/** Guards against modes the client no longer offers, like `NONE` from an older dataprovider row. */
export function isStorageEfficiency(value: string): value is StorageEfficiency {
  return value in DATA_REDUCTION_RATIO_BOUNDS;
}

// ── Output ──

export interface MemoryBreakdownItem {
  label: string;
  valueGB: number;
  percent: number;
}

export interface CpuBreakdownItem {
  label: string;
  valueCores: number;
  percent: number;
}

/** The capacity waterfall as sent by the engine. The client derives no figures from it. */
export interface QuickSizerCapacityWaterfall {
  rawCapacityGb: number;
  /** Logical usable capacity. With dedup on it can exceed raw. */
  logicalUsableGb: number;
  /** `capacity.overheadBreakdown`, in waterfall order, in GB. */
  overheads: QuickSizerApiCapacitySegment[];
}

/** One row of the max-VM table, as sent in `vmSizing[]`. */
export interface QuickSizerVmSizingRow {
  sizeId: string;
  label: string;
  vcpu: number;
  ramGb: number;
  diskGb: number;
  maxVms: number;
  limitingResource: LimitingResource;
}

/** The FTT/RAID the result was sized with. */
export interface QuickSizerDataProtection {
  hostFailuresToTolerate: number;
  raid: string;
  autoSelected: boolean;
}

export function dataProtectionLabel(dp: QuickSizerDataProtection): string {
  return `FTT ${dp.hostFailuresToTolerate} · ${dp.raid}${dp.autoSelected ? ' (Auto)' : ''}`;
}

/** One heading and its lines in the Sizing assumptions dialog. */
export interface QuickSizerAssumptionGroup {
  title: string;
  items: string[];
}

export interface QuickSizerOutput {
  memoryBreakdown: MemoryBreakdownItem[];
  cpuBreakdown: CpuBreakdownItem[];
  /** Logical, after data reduction. */
  usableCapacityGb: number;
  availableMemoryGb: number;
  /** Physical cores. */
  availableCores: number;
  totalIops: number;
  capacityWaterfall: QuickSizerCapacityWaterfall;
  vmSizing: QuickSizerVmSizingRow[];
  licensingAlert: QuickSizerApiLicensingAlert | null;
  assumptionGroups: QuickSizerAssumptionGroup[];
  dataProtection: QuickSizerDataProtection | null;
  /** Average-use and growth defaults the VM counts were sized with. */
  workloadDemand: QuickSizerWorkloadDemand;
  /** Every selected component at the size the engine charged. */
  componentSizing: QuickSizerApiComponentSizing[];
  /** VMs the component sizes were picked for. */
  componentSizingVmCount: number;
  /** Identifies this calculation; a dismissed licensing banner comes back when it changes. */
  calculatedAt: string;
}

/** View spelling of {@link ApiLimitingResource}. */
export type LimitingResource = 'CPU' | 'Memory' | 'Storage' | 'EXCEEDS_SINGLE_HOST' | 'VM_DENSITY';

export interface QuickSizerVmClass {
  sizeId: string;
  label: string;
  vcpu: number;
  ramGb: number;
  diskGb: number;
}

export interface QuickSizerHostLimits {
  minHostsManagementDomain: number;
  maxHosts: number;
}

// ── Wizard input state ──

export type HardwareConfigMode = 'readynode-based' | 'cpu-based';

/** Hardware step output. `cores` is per socket, so cores per host = cores × sockets. */
export interface HardwareSelection {
  mode: HardwareConfigMode;
  cores: number;
  sockets: number;
  clockSpeed: number;
  memory: number;
  drivesPerHost: number;
  driveSize: number;
  /** Host NIC speed in GbE. Picks the vSAN ReadyNode profile and so the IOPS ceiling. */
  networkSpeed: number;
  /** ReadyNode `systemName`; null on the CPU path. */
  systemName: string | null;
}

/** Splits `model:productId:profile`. Any other shape is all model, with no profile. */
export function readyNodeParts(systemName: string): { model: string; profile: string | null } {
  const match = /^(.+):\d+:([^:]+)$/.exec(systemName);
  return match ? { model: match[1], profile: match[2] } : { model: systemName, profile: null };
}

/** Turns `model:productId:profile` into `model (profile)`. Any other shape is shown as-is. */
export function readyNodeDisplayName(systemName: string): string {
  const { model, profile } = readyNodeParts(systemName);
  return profile ? `${model} (${profile})` : model;
}

export function emptyHardwareSelection(mode: HardwareConfigMode = 'readynode-based'): HardwareSelection {
  return {
    mode,
    cores: 0,
    sockets: 0,
    clockSpeed: 0,
    memory: 0,
    drivesPerHost: 0,
    driveSize: 0,
    networkSpeed: 0,
    systemName: null,
  };
}

export type DomainComponentState = DomainComponent & { enabled: boolean };

/** Dataprovider `GET .../quicksizer/configs`. */
export interface QuickSizerConfigs {
  vcfVersions: string[];
  vsanArchitectures: string[];
  driveSizesTb: number[];
  fttRaidOptions: FttRaidOption[];
  storageEfficiencyOptions: StorageEfficiency[];
  vmClasses: QuickSizerVmClass[];
  hostLimits: QuickSizerHostLimits;
  defaults: { vcpuToCoreRatio: number };
}

/** Flat wizard snapshot stored in NGXS. The HTTP body is `QuickSizerApiRequest`. */
export interface QuickSizerRequest {
  vcfVersion: string;
  totalHosts: number;
  capacityDriveSize: number;
  capacityDrivesPerHost: number;
  storageEfficiency: StorageEfficiency;
  /**
   * Single combined data-reduction ratio, as in the forward sizer.
   * `QuickSizerRequestAdapterService` expands it into the API's dedup/compression pair.
   */
  dataReductionRatio: number;
  systemMemoryGB: number;
  cpuCoresPerHost: number;
  /** `${ftt}-${raid}` key. */
  fttRaidKey: string;
  hostRebuildReserve: boolean;
  operationalReserve: boolean;
  includeMgmtDomain: boolean;
  vcpuToCoreRatio: number;
  hardware: HardwareSelection;
}

export interface QuickSizerApiDomainSelection {
  included: boolean;
  componentNames: string[];
}

/** OpenAPI `QuickSizerRequest`. */
export interface QuickSizerApiRequest {
  product: { name: string; version: string };
  cluster: { hostCount: number };
  node: {
    cpu: { coresPerHost: number; socketsPerHost: number; clockSpeedGhz: number };
    memory: { systemMemoryPerHostGb: number };
    storage: { drivesPerHost: number; driveCapacityTb: number };
    network: { speedGbe: number };
  };
  vsanConfig: {
    architecture: string;
    /** When true, `raid` / `hostFailuresToTolerate` are omitted. */
    autoMode: boolean;
    raid?: string;
    hostFailuresToTolerate?: number;
    storageEfficiency: StorageEfficiency;
    dedupRatio: number;
    compressionRatio: number;
    operationalReserveEnabled: boolean;
    hostRebuildReserveEnabled: boolean;
  };
  vcfComponents: {
    numberOfWorkloads: number;
    managementDomain: QuickSizerApiDomainSelection;
    workloadDomain: QuickSizerApiDomainSelection;
  };
  workloadProfile: { vcpuToCoreRatio: number };
  readyNodeRef?: { systemName: string };
}

export interface QuickSizerApiCapacitySegment {
  label: string;
  valueGb: number;
  percentOfRaw: number;
}

export interface QuickSizerApiMemorySegment {
  label: string;
  valueGb: number;
  percent: number;
}

export interface QuickSizerApiCpuSegment {
  label: string;
  valueCores: number;
  percent: number;
}

/** API `limitingResource`, spelled as the engine sends it. */
export type ApiLimitingResource = 'CPU' | 'MEMORY' | 'STORAGE' | 'EXCEEDS_SINGLE_HOST' | 'VM_DENSITY';

export interface QuickSizerApiVmSizing {
  sizeId: string;
  label: string;
  vcpu: number;
  ramGb: number;
  diskGb: number;
  maxVms: number;
  limitingResource?: ApiLimitingResource | string;
}

/** Storage shortfall against the vSAN Capacity Add-On entitlement. */
export interface QuickSizerApiLicensingAlert {
  message: string;
  shortfallTb?: number;
}

/** One selected component at the size it was charged at. Figures are for one instance. */
export interface QuickSizerApiComponentSizing {
  domain: 'MANAGEMENT' | 'WORKLOAD' | string;
  /** Catalog `productName`, e.g. `VCENTER`. */
  name: string;
  defaultBrickSize?: string;
  brickSize?: string;
  vms: number;
  /** Sum of vCPU over the component's VMs. Not physical cores. */
  vcpu: number;
  memoryGb: number;
  storageGb: number;
}

/** OpenAPI `QuickSizerResponse`. */
export interface QuickSizerApiResponse {
  capacity: {
    rawCapacityGb: number;
    usableCapacityGb: number;
    overheadBreakdown: QuickSizerApiCapacitySegment[];
  };
  memory: {
    availableForWorkloadsGb: number;
    breakdown: QuickSizerApiMemorySegment[];
  };
  cpu: {
    availableCores: number;
    breakdown: QuickSizerApiCpuSegment[];
  };
  io: {
    totalIops: number;
  };
  vmSizing: QuickSizerApiVmSizing[];
  licensingAlert?: QuickSizerApiLicensingAlert | null;
  assumptionGroups: QuickSizerAssumptionGroup[];
  dataProtection?: QuickSizerDataProtection;
  workloadDemand: QuickSizerWorkloadDemand;
  componentSizing: QuickSizerApiComponentSizing[];
  componentSizingVmCount: number;
  meta: { generated: string };
}

/** One VM is charged vcpu × avgCpu × (1 + growth), RAM × avgMemory × (1 + growth), disk × (1 + growth). */
export interface QuickSizerWorkloadDemand {
  avgCpuUtilizationPercent: number;
  avgMemoryUtilizationPercent: number;
  growthPercent: number;
}

export type QuickSizerErrorStep = 'hardware' | 'storage' | 'generic';

export type QuickSizerErrorKind =
  | 'VSAN_OVERHEAD_EXCEEDS_CPU'
  | 'VSAN_OVERHEAD_EXCEEDS_MEMORY'
  | 'VSAN_OVERHEAD_EXCEEDS_STORAGE'
  | 'UNDERSIZED_FOR_VCF'
  | 'REFERENCE_DATA_MISSING'
  /** Client-side: a 200 whose storage, RAM or cores left for workloads is 0. */
  | 'NO_WORKLOAD_CAPACITY'
  | 'CONFIGS_FAILED'
  | 'VALIDATION'
  | 'UNKNOWN';

/** A cluster pool workloads draw from, spelled as the backend's `shortResources`. */
export type WorkloadPool = 'STORAGE' | 'MEMORY' | 'CPU';

/** `hard`: nothing was sized. `soft`: turning something off gives a result. */
export type QuickSizerErrorSeverity = 'hard' | 'soft';

export interface QuickSizerStepError {
  step: QuickSizerErrorStep;
  kind: QuickSizerErrorKind;
  severity: QuickSizerErrorSeverity;
  messageKey: string;
  /** What to change. Narrowed to the short resource(s) when the backend sends `shortResources`. */
  remedy?: string;
  /** The backend's own message. */
  detail?: string;
}

/** One slice of a stacked resource bar. */
export interface ResourceSegment {
  label: string;
  value: number;
  unit: string;
  percent: number;
  color: string;
  /** Shown behind an info icon next to the legend label. */
  tooltip?: string;
}
